import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Loader2, ArrowRight, Handshake, Briefcase, ListChecks } from "lucide-react";

import SEO from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import ProjectListingCard from "@/components/project-listings/ProjectListingCard";
import PartnerApplicationForm from "@/components/project-listings/PartnerApplicationForm";
import { publicClient } from "@/lib/apiClient";
import {
  PROJECT_TYPES,
  PROJECT_DIVISIONS,
  PROJECT_TYPE_LABEL,
  DIVISION_LABEL,
} from "@/data/projectListingTemplates";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Project Listings" }];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const PARTNER_GUIDELINES = [
  { title: "Active Insurance", body: "Trade partners must have active general liability (and workers comp where required) before scope assignment." },
  { title: "Clear Communication", body: "Returns calls and texts. Shows up. Updates Black Ridge on progress and blockers." },
  { title: "Clean Workmanship", body: "We protect our brand on every property. Partners are expected to do the same." },
  { title: "Realistic Pricing", body: "Honest bids and accurate scoping beat lowball numbers. We pay on time and expect the same in return." },
];

/**
 * Project Listings (Public) — `/project-listings`
 *
 * Sections:
 *   1. Hero (2 CTAs)
 *   2. Partner guidelines
 *   3. Open project listings (with project-type + division filters)
 *   4. Partner application form (with prefill from listing CTA)
 */
export default function ProjectListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [divisionFilter, setDivisionFilter] = useState("all");
  const [prefill, setPrefill] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicClient
      .get("/project-listings")
      .then((res) => {
        if (cancelled) return;
        setListings(Array.isArray(res.data) ? res.data : []);
        setError("");
      })
      .catch(() => {
        if (cancelled) return;
        setError("We couldn’t load project opportunities right now. Please try again in a moment.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredListings = useMemo(() => {
    return listings.filter((l) => {
      if (typeFilter !== "all" && l.project_type !== typeFilter) return false;
      if (divisionFilter !== "all" && l.division !== divisionFilter) return false;
      return true;
    });
  }, [listings, typeFilter, divisionFilter]);

  const handleApply = useCallback((listing) => {
    setPrefill({
      _stamp: Date.now(),
      listing_id: listing.id,
      project_title: listing.title,
    });
    scrollToId("partner-apply");
  }, []);

  return (
    <>
      <SEO
        title="Construction Project Opportunities Austin TX | Black Ridge Construction"
        description="View current project opportunities and scope needs from Black Ridge Construction. Subcontractors and trade partners can apply for available scopes in Austin and Central Texas."
      />

      {/* ────────────────────── Hero ────────────────────── */}
      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-20">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">Trade Partner Opportunities</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">Project Opportunities for Trade Partners</h1>
            <p className="mt-4 text-gray-300 leading-relaxed">
              Black Ridge Construction works with qualified subcontractors and trade partners across residential, roofing, remodeling, maintenance, exterior, and commercial scopes. Review current opportunities and submit your information for consideration.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" onClick={() => scrollToId("open-scopes")} className="br-btn-primary" data-testid="projects-hero-scopes">
                <Briefcase size={14} /> View Open Scopes
              </button>
              <button type="button" onClick={() => scrollToId("partner-apply")} className="br-btn-secondary" data-testid="projects-hero-apply">
                <Handshake size={14} /> Apply as a Trade Partner
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────── Partner Guidelines ──────────────────── */}
      <section className="section-pad bg-br-paper">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">What We Look For</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Partner Standards</h2>
            <p className="mt-3 text-gray-700">Black Ridge selects trade partners who can show up, communicate, and protect the homeowner experience as well as we do.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="partner-guidelines">
            {PARTNER_GUIDELINES.map((g) => (
              <div key={g.title} className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="w-10 h-10 rounded-md bg-br-paper-2 flex items-center justify-center text-br-bronze mb-3">
                  <ListChecks size={18} />
                </div>
                <div className="font-display tracking-widest uppercase text-base text-br-black">{g.title}</div>
                <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">{g.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────── Open Scopes ──────────────────────── */}
      <section className="section-pad bg-white" id="open-scopes">
        <div className="container-br">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="br-eyebrow">Currently Open</div>
              <h2 className="br-headline text-3xl sm:text-4xl mt-3">Open Project Scopes</h2>
              <p className="mt-2 text-sm text-gray-600 max-w-xl">{listings.length} open opportunit{listings.length === 1 ? "y" : "ies"} — use the filters to narrow by project type or division.</p>
            </div>
          </div>

          {/* Filters */}
          <div className="grid sm:grid-cols-2 gap-3 mb-6 max-w-2xl" data-testid="project-filters">
            <div>
              <label className="label-br">Project Type</label>
              <select className="select-br" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="project-filter-type">
                <option value="all">All Types</option>
                {PROJECT_TYPES.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-br">Division</label>
              <select className="select-br" value={divisionFilter} onChange={(e) => setDivisionFilter(e.target.value)} data-testid="project-filter-division">
                <option value="all">All Divisions</option>
                {PROJECT_DIVISIONS.map((d) => <option key={d.slug} value={d.slug}>{d.label}</option>)}
              </select>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-16 text-gray-500" data-testid="projects-loading">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading open scopes…
            </div>
          )}
          {!loading && error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="projects-error">{error}</div>
          )}
          {!loading && !error && filteredListings.length === 0 && (
            <div className="bg-br-paper border border-gray-200 rounded-lg p-10 text-center" data-testid="projects-empty">
              <div className="w-12 h-12 mx-auto rounded-full bg-white flex items-center justify-center text-br-bronze mb-3">
                <Briefcase size={20} />
              </div>
              <div className="font-display tracking-widest uppercase text-lg text-br-black">No open scopes match your filters</div>
              <p className="mt-2 text-sm text-gray-700 max-w-md mx-auto">
                We&rsquo;re always interested in hearing from qualified trade partners — submit your information below and we&rsquo;ll keep you in mind as scopes come up.
              </p>
              <button type="button" onClick={() => scrollToId("partner-apply")} className="br-btn-dark mt-5" data-testid="projects-empty-apply">
                Apply as a Trade Partner <ArrowRight size={14} />
              </button>
            </div>
          )}
          {!loading && !error && filteredListings.length > 0 && (
            <div className="grid lg:grid-cols-2 gap-5">
              {filteredListings.map((l) => (
                <ProjectListingCard key={l.id} listing={l} onApply={handleApply} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ────────────────── Partner Application Form ───────────────────── */}
      <section className="section-pad bg-br-paper">
        <div className="container-br">
          <PartnerApplicationForm ref={formRef} prefill={prefill} />
        </div>
      </section>
    </>
  );
}
